import './new-pma.scss';
import Header from '../../../organisms/header/header.js';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import { Alert, InputBase, Snackbar } from '@mui/material';
import Grid from "@mui/material/Grid2";
import Input from "../../../atoms/input";
import apiClient from "../../../../utils/apiClient";
import { useForm } from 'react-hook-form';
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import FormControlLabel from "@mui/material/FormControlLabel";

function NewPma() {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const navigate = useNavigate();

  const [areaCode, setAreaCode] = useState('');
  const [countries, setCountries] = useState([]);
  const [countriesToManage, setCountriesToManage] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');

  const [canNominate, setCanNominate] = useState(false);
  const handleNominateChange = (event) => {
    setCanNominate(event.target.checked); // Actualiza el estado con el valor del checkbox
  };

  // Teléfono principal
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Teléfonos por país gestionado: { [areaCode]: { value, error, touched } }
  const [managedPhones, setManagedPhones] = useState({});

  const isEmptyPhone = (v) => !v || v.replace(/\D/g, '').length === 0;
  const hasOnlyPrefix = (v) => {
    const digits = (v || '').replace(/\D/g, '');
    return digits.length > 0 && digits.length <= 3; // solo prefijo (1–3 dígitos)
  };

  const [phoneAreaCode] = useState(() => {
    const user = JSON.parse(sessionStorage.getItem('loggedUser'));
    return user?.area_code?.toLowerCase() || '';
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Helpers país → ISO2 válido
  const normalizeAreaCode = (code) => (code === 'LUX' ? 'LU' : code);
  const toPhoneIso2 = (code) => (normalizeAreaCode(code || '').toLowerCase());

  // Joi (teléfonos por país OPCIONALES con fallback al general)
  const schema = Joi.object({
    name: Joi.string().required().messages({ 'string.empty': t('alert-name') }),
    last_name: Joi.string().required().messages({ 'string.empty': t('alert-last-name') }),
    email: Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
      'string.email': t('alert-email-format'),
      'string.empty': t('alert-email'),
    }),
    prefix_phone: Joi.string().pattern(/^\+[0-9]{1,3}$/).required().messages({
      'string.pattern.base': t('alert-prefix-format'),
      'string.empty': t('alert-prefix'),
    }),
    phone: Joi.string().pattern(/^[0-9]{7,15}$/).required().messages({
      'string.pattern.base': t('alert-phone-format'),
      'string.empty': t('alert-phone'),
    }),
    area_code: Joi.string().required().messages({ 'string.empty': t('alert-country') }),
    preferred_language: Joi.string().required().messages({ 'string.empty': t('alert-language') }),
    role: Joi.string().required().messages({ 'string.empty': t('alert-role') }),
    managed_country: Joi.array().min(1).required().messages({
      'array.base': t('alert-country'),
      'array.min': t('alert-country'),
      'any.required': t('alert-country')
    }),
    managed_phones: Joi.object().custom((value, helpers) => {
      const form = helpers?.state?.ancestors?.[0] || {};
      const list = form.managed_country || [];
      const prefixRe = /^\+[0-9]{1,3}$/;
      const phoneRe = /^[0-9]{7,15}$/;

      for (const code of list) {
        const entry = value?.[code];

        // Si NO hay datos para ese país, no validamos (se aplicará fallback al enviar)
        const hasAny =
          !!(entry?.prefix_phone && entry.prefix_phone.trim()) ||
          !!(entry?.phone && entry.phone.toString().trim());
        if (!hasAny) continue;

        if (!prefixRe.test(entry.prefix_phone || '')) {
          return helpers.error('any.custom', { message: `${t('alert-prefix-format')} (${code})` });
        }
        if (!phoneRe.test(entry.phone || '')) {
          return helpers.error('any.custom', { message: `${t('alert-phone-format')} (${code})` });
        }
      }

      return value;
    }).messages({ 'any.custom': '{{#message}}' }),
  });

  const { register, handleSubmit, formState, setValue, setFocus } = useForm({
    resolver: joiResolver(schema),
    mode: 'all',
    defaultValues: {
      managed_country: [],
      managed_phones: {},
    },
  });
  const { errors } = formState;

  // onInvalid: marcamos touched para mostrar errores
  const onInvalid = () => {
    // Teléfono principal
    setPhoneTouched(true);
    if (isEmptyPhone(phone)) {
      setPhoneError(t('alert-phone'));
      setValue('prefix_phone', '');
      setValue('phone', '');
    } else {
      const parsed = parsePhoneNumberFromString(phone);
      if (!parsed || !parsed.isValid() || !parsed.nationalNumber) {
        setPhoneError(t('alert-phone-format'));
      } else {
        setPhoneError('');
        setValue('prefix_phone', `+${parsed.countryCallingCode}`);
        setValue('phone', parsed.nationalNumber);
      }
    }

    // Teléfonos por país: si hay algo, validamos; si están vacíos, sin error (fallback en submit)
    setManagedPhones((prev) => {
      const next = { ...prev };
      countriesToManage.forEach((code) => {
        const curr = next[code] || { value: '', error: '', touched: false };
        const val = curr.value;

        next[code] = { ...curr, touched: true };

        if (isEmptyPhone(val) || hasOnlyPrefix(val)) {
          next[code].error = '';
          setValue(`managed_phones.${code}.prefix_phone`, '');
          setValue(`managed_phones.${code}.phone`, '');
        } else {
          const parsed = parsePhoneNumberFromString(val);
          if (!parsed || !parsed.isValid() || !parsed.nationalNumber) {
            next[code].error = t('alert-phone-format');
          } else {
            next[code].error = '';
            setValue(`managed_phones.${code}.prefix_phone`, `+${parsed.countryCallingCode}`);
            setValue(`managed_phones.${code}.phone`, parsed.nationalNumber);
          }
        }
      });
      return next;
    });
  };

  // Obtener países (excluyendo EN y ordenados)
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get(`${process.env.REACT_APP_API_URL}/hac/countries`);
        const list = response.data
          .filter(country => country.area_code !== "EN")
          .sort((a, b) =>
            t(`countries-${a.area_code}`).localeCompare(t(`countries-${b.area_code}`))
          );
        setCountries(list);
      } catch (error) {
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    };
    fetchCountries();
  }, [t]);

  // Idiomas según país principal
  useEffect(() => {
    if (areaCode && countries.length) {
      const selectedCountry = countries.find((c) => c.area_code === areaCode);
      if (selectedCountry) {
        setLanguages(selectedCountry.language || []);
        setSelectedLanguage('');
        setValue('preferred_language', '');
      }
    }
  }, [areaCode, countries, setValue]);

  // Inicializa/limpia teléfonos por país al cambiar managed_country
  useEffect(() => {
    setValue('managed_country', countriesToManage);

    setManagedPhones((prev) => {
      const next = { ...prev };

      // añadir nuevos
      countriesToManage.forEach((code) => {
        const iso2Lower = toPhoneIso2(code);
        const iso2Upper = iso2Lower.toUpperCase();
        if (!next[code]) {
          try {
            const calling = getCountryCallingCode(iso2Upper);
            next[code] = { value: `+${calling}`, error: '', touched: false };
            setValue(`managed_phones.${code}.prefix_phone`, `+${calling}`);
            setValue(`managed_phones.${code}.phone`, '');
          } catch {
            next[code] = { value: '', error: '', touched: false };
            setValue(`managed_phones.${code}.prefix_phone`, '');
            setValue(`managed_phones.${code}.phone`, '');
          }
        }
      });

      // eliminar los que ya no estén seleccionados
      Object.keys(next).forEach((code) => {
        if (!countriesToManage.includes(code)) {
          delete next[code];
          setValue(`managed_phones.${code}.prefix_phone`, undefined);
          setValue(`managed_phones.${code}.phone`, undefined);
        }
      });

      return next;
    });
  }, [countriesToManage, setValue]);

  // Cambio país principal
  const handleCountryChange = (e) => {
    const selectedAreaCode = e.target.value;
    setAreaCode(selectedAreaCode);

    if (!selectedAreaCode) {
      setLanguages([]);
      setSelectedLanguage('');
      setValue('preferred_language', '');
    }
  };

  // Submit
  const onSubmit = async (data) => {
    // 1) Validar teléfono general
    const fullPhone = parsePhoneNumberFromString(phone);
    if (isEmptyPhone(phone)) {
      setPhoneError(t('alert-phone'));
      setFocus('phone');
      return;
    }
    if (!fullPhone || !fullPhone.isValid() || !fullPhone.nationalNumber) {
      setPhoneError(t('alert-phone-format'));
      setFocus('phone');
      return;
    }
    setPhoneError('');

    // 2) Rellenar teléfonos por país que estén vacíos o solo con prefijo con el general
    const patchedManagedPhones = { ...(data.managed_phones || {}) };

    countriesToManage.forEach((code) => {
      const state = managedPhones[code] || { value: '' };
      const rawVal = state.value || '';

      const entry = patchedManagedPhones[code] || {};
      const hasRHF =
        (entry.prefix_phone && entry.prefix_phone.trim()) &&
        (entry.phone && entry.phone.toString().trim());

      const emptyOrOnlyPrefix = isEmptyPhone(rawVal) || hasOnlyPrefix(rawVal);

      if (!hasRHF || emptyOrOnlyPrefix) {
        patchedManagedPhones[code] = {
          prefix_phone: data.prefix_phone,
          phone: data.phone,
        };
      }
    });

    data.managed_phones = patchedManagedPhones;

    // Actualizar visualmente los PhoneInput de abajo para mostrar el general si se ha rellenado así
    setManagedPhones((prev) => {
      const next = { ...prev };
      countriesToManage.forEach((code) => {
        const prevState = next[code] || { value: '', error: '', touched: false };
        const val = prevState.value || '';
        const entry = patchedManagedPhones[code];

        if (entry && (isEmptyPhone(val) || hasOnlyPrefix(val))) {
          next[code] = {
            ...prevState,
            value: phone,
            error: '',
            touched: true,
          };
        }
      });
      return next;
    });

    countriesToManage.forEach((code) => {
      const entry = patchedManagedPhones[code];
      if (entry) {
        setValue(`managed_phones.${code}.prefix_phone`, entry.prefix_phone);
        setValue(`managed_phones.${code}.phone`, entry.phone);
      }
    });

    // 3) Construir area_code_visibility [{ area_code, prefix_phone, phone }]
    const area_code_visibility = (data.managed_country || []).map((code) => {
      const entry = data.managed_phones?.[code] || {
        prefix_phone: data.prefix_phone,
        phone: data.phone,
      };
      return {
        area_code: code,
        prefix_phone: entry.prefix_phone || data.prefix_phone,
        phone: entry.phone || data.phone,
      };
    });

    const payload = {
      email: data.email,
      name: data.name,
      last_name: data.last_name,
      prefix_phone: data.prefix_phone, // general
      phone: data.phone,               // general
      area_code: data.area_code,
      preferred_language: data.preferred_language,
      area_code_visibility,
      is_nominator: canNominate
    };

    let url;
    if (data.role === 'pma') {
      url = `${process.env.REACT_APP_API_URL}/pma`;
    } else if (data.role === 'pmaGM') {
      url = `${process.env.REACT_APP_API_URL}/pma/general-manager`;
    }

    try {
      const response = await apiClient.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200 || response.status === 201) {
        setSnackbarMessage(t('admin-new-pma-successful'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => navigate("/pma/main/pma"), 2000);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setSnackbarMessage(t('alert-duplicate'));
        setSnackbarSeverity('error');
      } else {
        setSnackbarMessage(t('common-internal-server-error'));
        setSnackbarSeverity('error');
      }
      setSnackbarOpen(true);
    }
  };

  return (
    <>
      {token && role === 'pmaGM' ? (
        <div className="App">
          <Header />
          <main className="new-pma">
            <section className="form">
              <div className="inner">
                <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                  <Link to="/pma/main/pma" className="back-button">
                    <span className="icon-chevron-left"></span> {t('common-back')}
                  </Link>
                  <h2>{t('admin-new-pma')}</h2>

                  <div>
                    <p><strong>{t('common-basic-info')}</strong></p>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="name"
                            label={t('common-name')}
                            error={errors?.name}
                            {...register('name')}
                          />
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="last_name"
                            label={t('common-last-name')}
                            error={errors?.last_name}
                            {...register('last_name')}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="email"
                            label={t('common-email')}
                            error={errors?.email}
                            {...register('email')}
                          />
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="phone">{t('common-phone')}</label>
                          <PhoneInput
                            defaultCountry={phoneAreaCode}
                            value={phone}
                            onChange={(value) => {
                              setPhone(value);
                              if (phoneTouched) {
                                if (isEmptyPhone(value)) {
                                  setPhoneError(t('alert-phone'));
                                  setValue('prefix_phone', '');
                                  setValue('phone', '');
                                  return;
                                }
                                const parsed = parsePhoneNumberFromString(value);
                                if (parsed && parsed.isValid() && parsed.nationalNumber) {
                                  setPhoneError('');
                                  setValue('prefix_phone', `+${parsed.countryCallingCode}`);
                                  setValue('phone', parsed.nationalNumber);
                                } else {
                                  setPhoneError(t('alert-phone-format'));
                                }
                              }
                            }}
                            onBlur={() => {
                              setPhoneTouched(true);
                              if (isEmptyPhone(phone)) {
                                setPhoneError(t('alert-phone'));
                                setValue('prefix_phone', '');
                                setValue('phone', '');
                                return;
                              }
                              const parsed = parsePhoneNumberFromString(phone);
                              if (!parsed || !parsed.isValid() || !parsed.nationalNumber) {
                                setPhoneError(t('alert-phone-format'));
                              } else {
                                setPhoneError('');
                                setValue('prefix_phone', `+${parsed.countryCallingCode}`);
                                setValue('phone', parsed.nationalNumber);
                              }
                            }}
                          />
                          {phoneError && <span className="error-message">{phoneError}</span>}

                          <input type="hidden" {...register('prefix_phone')} />
                          <input type="hidden" {...register('phone')} />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 1, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="area_code">{t('common-country')}</label>
                          <select
                            name="area_code"
                            id="area_code"
                            className="normal"
                            {...register('area_code')}
                            onChange={handleCountryChange}
                          >
                            <option value="">{t('common-select-country')}</option>
                            {countries.map((country) => (
                              <option key={country.area_code} value={country.area_code}>
                                {t(`countries-${country.area_code}`)}
                              </option>
                            ))}
                          </select>
                          {errors.area_code && (
                            <span className="input__error">{errors.area_code.message}</span>
                          )}
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 1, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="preferred_language">{t('common-select-language')}</label>
                          <select
                            name="preferred_language"
                            id="preferred_language"
                            className="normal"
                            {...register('preferred_language')}
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                          >
                            <option value="">{t('common-select-language')}</option>
                            {languages.map((language, index) => (
                              <option key={index} value={language}>
                                {t(`languages-${language}`)}
                              </option>
                            ))}
                          </select>
                          {errors.preferred_language && (
                            <span className="input__error">{errors.preferred_language.message}</span>
                          )}
                        </div>
                      </Grid>
                    </Grid>
                    <br /><br />
                    <p><strong>{t('common-settings-pma')}</strong></p>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="role">{t('common-admin-role')}</label>
                          <select
                            name="role"
                            id="role"
                            className="normal"
                            {...register('role')}
                          >
                            <option value="">{t('common-select-role-admin')}</option>
                            <option value="pmaGM">{t('common-role-pmaGM')}</option>
                            <option value="pma">{t('common-role-pma')}</option>
                          </select>
                          {errors.role && (
                            <span className="input__error">{errors.role.message}</span>
                          )}
                        </div>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="managed_country">{t('common-manage-country')}</label>
                          <FormControl fullWidth>
                            <Select
                              id="managed_country"
                              className="multiselect"
                              variant="filled"
                              multiple
                              value={countriesToManage}
                              onChange={(e) => {
                                const { value } = e.target;
                                const arr = Array.isArray(value) ? value : [value];
                                setCountriesToManage(arr);
                                setValue('managed_country', arr);
                              }}
                              input={
                                <InputBase
                                  name="managed_country"
                                  id="managed_country"
                                  label={t('common-manage-country')}
                                />
                              }
                              renderValue={(selected) => selected.join(', ')}
                            >
                              {countries.map((country) => (
                                <MenuItem key={country.area_code} value={country.area_code} className="multiselect-options">
                                  <Checkbox checked={countriesToManage.includes(country.area_code)} />
                                  <ListItemText primary={t(`countries-${country.area_code}`)} />
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.managed_country && (
                              <span className="input__error">{errors.managed_country.message}</span>
                            )}
                          </FormControl>
                        </div>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={canNominate}
                              onChange={handleNominateChange}
                            />
                          }
                          label={t('admin-new-admin-nominate')}
                        />
                      </Grid>
                    </Grid>
                    <br /><br />
                    {/* Teléfonos por país (opcionales con fallback al general en submit) */}
                    <Grid container columnSpacing={3} rowSpacing={0}>
                      {countriesToManage.map((code) => {
                        const iso2Lower = toPhoneIso2(code);
                        const labelCountry = t(`countries-${code}`);
                        const state = managedPhones[code] || { value: '', error: '', touched: false };

                        return (
                          <Grid key={code} size={{ xs: 12, sm: 12, md: 6 }}>
                            <div className="form-control">
                              <label>{t('common-phone')} — {labelCountry}</label>
                              <PhoneInput
                                defaultCountry={iso2Lower}
                                value={state.value}
                                onChange={(val) => {
                                  setManagedPhones((prev) => {
                                    const next = { ...prev, [code]: { ...state, value: val } };
                                    if (state.touched) {
                                      if (isEmptyPhone(val) || hasOnlyPrefix(val)) {
                                        next[code].error = '';
                                        setValue(`managed_phones.${code}.prefix_phone`, '');
                                        setValue(`managed_phones.${code}.phone`, '');
                                        return next;
                                      }
                                      const parsed = parsePhoneNumberFromString(val);
                                      if (parsed && parsed.isValid() && parsed.nationalNumber) {
                                        next[code].error = '';
                                        setValue(`managed_phones.${code}.prefix_phone`, `+${parsed.countryCallingCode}`);
                                        setValue(`managed_phones.${code}.phone`, parsed.nationalNumber);
                                      } else {
                                        next[code].error = t('alert-phone-format');
                                      }
                                    }
                                    return next;
                                  });
                                }}
                                onBlur={() => {
                                  setManagedPhones((prev) => {
                                    const next = { ...prev, [code]: { ...state, touched: true } };
                                    if (isEmptyPhone(state.value) || hasOnlyPrefix(state.value)) {
                                      next[code].error = '';
                                      setValue(`managed_phones.${code}.prefix_phone`, '');
                                      setValue(`managed_phones.${code}.phone`, '');
                                      return next;
                                    }
                                    const parsed = parsePhoneNumberFromString(state.value);
                                    if (!parsed || !parsed.isValid() || !parsed.nationalNumber) {
                                      next[code].error = t('alert-phone-format');
                                    } else {
                                      next[code].error = '';
                                      setValue(`managed_phones.${code}.prefix_phone`, `+${parsed.countryCallingCode}`);
                                      setValue(`managed_phones.${code}.phone`, parsed.nationalNumber);
                                    }
                                    return next;
                                  });
                                }}
                              />
                              {state.error && <span className="error-message">{state.error}</span>}

                              {/* Hidden inputs RHF */}
                              <input type="hidden" {...register(`managed_phones.${code}.prefix_phone`)} />
                              <input type="hidden" {...register(`managed_phones.${code}.phone`)} />
                            </div>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {errors.managed_phones && (
                      <div style={{ marginTop: 8 }}>
                        <span className="input__error">{errors.managed_phones.message}</span>
                      </div>
                    )}

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <button className="btn" type="submit">
                          {t('admin-new-pma-action')}
                        </button>
                      </Grid>
                    </Grid>
                  </div>
                </form>
              </div>
            </section>
          </main>
          <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
            <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login" />
      )}
    </>
  );
}

export default NewPma;
